/**
 * DisplayNameSourceIndicator
 *
 * Two independent A/B flags — both can be on simultaneously:
 *   - display_name_source_indicator  → shows a Chip badge for non-user sources
 *   - display_name_source_tooltip    → shows an info icon (ℹ) with a Tooltip for ALL sources
 *
 * Phase 1: sourceLabel is always undefined.
 * Phase 2: pass sourceLabel={row.manufacturer_name} when source is 'manufacturer'.
 */

import React from 'react';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  DISPLAY_NAME_SOURCE,
  DisplayNameSource,
} from '../services/api/domains/products/types';
import { useABTest } from '../contexts/ABTestContext';

export interface DisplayNameSourceIndicatorProps {
  /**
   * The computed source of the display name. Widened beyond the 4-value
   * product `DisplayNameSource` union (issue #653, Sub-task 11.1) so
   * manufacturer callers can pass one of the finer-grained
   * `MANUFACTURER_NAME_ROLE_LABELS` role strings (e.g. `'gleif_name'`) —
   * unrecognized values fall back to `sourceLabel`/the raw string below,
   * they don't need a `SOURCE_CONFIG` entry.
   */
  displayNameSource: DisplayNameSource | string | null | undefined;
  /**
   * Optional label override for badge/tooltip text.
   * Phase 1: always undefined.
   * Phase 2: pass manufacturer_name when source is 'manufacturer'.
   */
  sourceLabel?: string;
  children?: React.ReactNode;
}

/** Per-source configuration */
const SOURCE_CONFIG: Record<
  string,
  {
    label: string;
    color: 'default' | 'primary' | 'secondary' | 'info' | 'warning';
    ariaLabel: string;
  }
> = {
  [DISPLAY_NAME_SOURCE.ORANGE_BOOK]: {
    // TODO(icons): replace with final assets — see Task 18
    label: 'ORANGE BOOK',
    color: 'warning',
    ariaLabel: 'Name sourced from FDA Orange Book',
  },
  [DISPLAY_NAME_SOURCE.CAS]: {
    // TODO(icons): replace with final assets — see Task 18
    label: 'CAS REGISTRY',
    color: 'default',
    ariaLabel: 'Name sourced from CAS registry',
  },
  [DISPLAY_NAME_SOURCE.USER]: {
    label: 'LGM Product Name',
    color: 'default',
    ariaLabel: 'LGM-assigned product name',
  },
  [DISPLAY_NAME_SOURCE.MANUFACTURER]: {
    // sourceLabel (manufacturer_name) overrides this label when available
    label: 'Manufacturer Catalogue',
    color: 'info',
    ariaLabel: 'Name sourced from manufacturer catalogue',
  },
};

const DisplayNameSourceIndicator: React.FC<DisplayNameSourceIndicatorProps> = ({
  displayNameSource,
  sourceLabel,
  children,
}) => {
  const { getTestValue } = useABTest();
  const indicatorEnabled = getTestValue('display_name_source_indicator');
  const tooltipVariant = getTestValue('display_name_source_tooltip');

  // Guard: both flags off, or no source
  if ((!indicatorEnabled && !tooltipVariant) || !displayNameSource) {
    return children ? <>{children}</> : null;
  }

  const isUserSource = displayNameSource === DISPLAY_NAME_SOURCE.USER;
  const config = SOURCE_CONFIG[displayNameSource];
  const badgeText = sourceLabel ?? config?.label ?? displayNameSource;
  const ariaLabelText = sourceLabel
    ? `Name sourced from ${sourceLabel}`
    : (config?.ariaLabel ?? `Name source: ${displayNameSource}`);

  // Chip: shown only for non-user sources when indicator flag is on
  const showChip = indicatorEnabled && !isUserSource;

  // Info icon tooltip: shown for ALL sources when tooltip flag is on
  const showTooltip = tooltipVariant;

  if (!showChip && !showTooltip) {
    return children ? <>{children}</> : null;
  }

  return (
    <Box
      style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
      }}>
      <Box
        style={{
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
        {children}
      </Box>
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          flexShrink: 0,
          marginLeft: 'auto',
          paddingLeft: 4,
        }}>
        {showChip && (
          <Chip
            label={badgeText}
            size="small"
            color={config?.color ?? 'default'}
            aria-label={ariaLabelText}
            style={{
              height: 16,
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.04em',
              flexShrink: 0,
              pointerEvents: 'none',
            }}
          />
        )}
        {showTooltip && (
          <Tooltip title={badgeText} placement="top" arrow>
            <InfoOutlinedIcon
              aria-label={ariaLabelText}
              style={{
                fontSize: 14,
                color: '#9E9E9E',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            />
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default DisplayNameSourceIndicator;
